import {
  Children,
  cloneElement,
  isValidElement,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import type { AvatarProps, AvatarStackProps } from './types.js';

/** Deterministic name → hue hash (0–360). Same name always produces the
 *  same pastel, so a user's avatar tint stays stable across the app. */
function hashHue(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash << 5) - hash + name.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash) % 360;
}

function deriveInitials(name: string): string {
  const trimmed = name.trim();
  if (trimmed === '') return '?';
  const parts = trimmed.split(/\s+/).filter(Boolean);
  const firstPart = parts[0] ?? '';
  const lastPart = parts[parts.length - 1] ?? '';
  if (parts.length === 1) {
    /* For CJK + single-word Latin, show the first glyph. */
    return Array.from(firstPart)[0] ?? '?';
  }
  const first = Array.from(firstPart)[0] ?? '';
  const last = Array.from(lastPart)[0] ?? '';
  return `${first}${last}`.toUpperCase();
}

/** User avatar with a three-tier fallback:
 *    1. `children` (custom icon / content) if provided
 *    2. `src` image, with automatic fallback on error
 *    3. `initials` (explicit or derived from `name`) on a deterministic
 *       hashed-colour background
 *
 *  Initials work well for cached user directories where avatar images
 *  haven't loaded yet; the hashed colour means a user is visually
 *  recognisable even without their picture.
 *
 *  When `interactive` is true, the root element becomes a button with
 *  focus-visible styling so an avatar can serve as a profile trigger.
 */
export function Avatar(props: AvatarProps): ReactNode {
  const {
    src,
    name,
    initials,
    children,
    size = 'md',
    shape = 'circle',
    status,
    interactive = false,
    color,
    onClick,
    className,
    'aria-label': ariaLabel,
  } = props;

  const [imgFailed, setImgFailed] = useState(false);
  const label = ariaLabel ?? name;
  const resolvedInitials = useMemo(() => initials ?? deriveInitials(name), [initials, name]);
  const hue = useMemo(() => hashHue(name), [name]);
  const hueStr = String(hue);
  const bg = color ?? `hsl(${hueStr} 55% 78%)`;
  const fg = color ?? `hsl(${hueStr} 55% 22%)`;

  const showImage = src !== undefined && !imgFailed && children === undefined;
  const showInitials = children === undefined && !showImage;

  const classes = [
    'vq-avatar',
    `vq-avatar--${size}`,
    `vq-avatar--${shape}`,
    status !== undefined && 'vq-avatar--has-status',
    interactive && 'vq-avatar--interactive',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const inner = (
    <>
      {showImage && (
        <img
          src={src}
          alt=""
          className="vq-avatar__img"
          onError={() => {
            setImgFailed(true);
          }}
          draggable="false"
        />
      )}
      {showInitials && (
        <span
          className="vq-avatar__initials"
          style={{ background: bg, color: fg }}
          aria-hidden="true"
        >
          {resolvedInitials}
        </span>
      )}
      {children !== undefined && <span className="vq-avatar__custom">{children}</span>}
      {status !== undefined && (
        <span className={`vq-avatar__status vq-avatar__status--${status}`} aria-hidden="true" />
      )}
    </>
  );

  if (interactive) {
    return (
      <button
        type="button"
        className={classes}
        onClick={onClick}
        aria-label={label}
        data-component="Avatar"
        data-size={size}
      >
        {inner}
      </button>
    );
  }

  return (
    <span
      className={classes}
      role="img"
      aria-label={label}
      data-component="Avatar"
      data-size={size}
    >
      {inner}
    </span>
  );
}

/** Overlapping row of avatars with a trailing "+N" chip when the count
 *  exceeds `max`. The chip itself is an Avatar with initials disabled —
 *  reusing the same size / shape chrome so the row reads as uniform. */
export function AvatarStack(props: AvatarStackProps): ReactNode {
  const {
    max = 3,
    overlap = '-6px',
    size = 'md',
    shape = 'circle',
    children,
    className,
    'aria-label': ariaLabel,
  } = props;
  const all = Children.toArray(children).filter(isValidElement);
  const visible = all.slice(0, max);
  const overflow = all.length - visible.length;

  const classes = ['vq-avatar-stack', `vq-avatar-stack--${size}`, className]
    .filter(Boolean)
    .join(' ');

  /* CSS variable drives the overlap spacing — adjacent avatars use
   * negative margin-left to pull onto the previous one. */
  const style = { '--vq-avatar-overlap': overlap } as React.CSSProperties;

  return (
    <div
      className={classes}
      style={style}
      role="group"
      aria-label={ariaLabel ?? `${String(all.length)} avatars`}
    >
      {visible.map((child, i) => {
        /* Force the stack's size/shape onto children so heterogeneous
         * props from callers still produce a visually uniform row. */
        const el = child as ReactElement<AvatarProps>;
        return cloneElement(el, {
          key: el.key ?? i,
          size,
          shape,
        });
      })}
      {overflow > 0 && (
        <span
          className={`vq-avatar vq-avatar--${size} vq-avatar--${shape} vq-avatar__overflow`}
          aria-label={`+${String(overflow)} more`}
        >
          <span className="vq-avatar__initials" aria-hidden="true">
            +{overflow}
          </span>
        </span>
      )}
    </div>
  );
}
